import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Table, 
  TableBody, 
  TableRow, 
  TableCell, 
  TableContainer, 
  TableHead, 
  Typography, 
  IconButton,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Skeleton
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Breadcrumb from 'components/Breadcrumb';
import { logger } from 'utils/logger';
import apiService from 'services/ApiService';
import { environment } from 'environments/environment';
import solutionsService, { UserSolution } from './services/SolutionsService';
import AddSolutionDialog from './components/AddSolutionDialog';
import { usersService } from './services/UsersService';
import ROLES from 'constants/roles';

// Create a component-specific logger
const log = logger.getLogger('UserSolutionsPage');

const UserSolutionsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { accountId, userId } = useParams<{ accountId: string; userId: string }>();
  
  const [solutions, setSolutions] = useState<UserSolution[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [userDataLoaded, setUserDataLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [hasFullAccess, setHasFullAccess] = useState<boolean>(false);
  const [userRoleDisplay, setUserRoleDisplay] = useState<string>("");
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch account name
  const fetchAccountName = useCallback(async () => {
    if (!accountId) return;
    
    try {
      const response = await apiService.makeRequest(
        'get', 
        `${environment.services.account.baseUrl}${environment.services.account.basePath}/accounts/${accountId}`
      );
      setAccountName(response?.name || `Account #${accountId}`);
    } catch (err) {
      log.error('Error fetching account details:', err);
      setAccountName(`Account #${accountId}`);
    }
  }, [accountId]);

  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    if (!userId || !accountId) return;
    
    try {
      const user = await usersService.getUser(Number(userId));
      setUserName(`${user.firstName} ${user.lastName}`);
      
      // Check if user has a role with full access
      const hasAdminPrivileges = user.roles?.some(r => r.name === ROLES.SOLUTION_ADMIN.name) || false;
      setHasFullAccess(hasAdminPrivileges);
      
      // Show the user's role display names
      setUserRoleDisplay(user.roles?.map(r => r.displayName).join(', ') || '');
      
      setUserDataLoaded(true);
    } catch (err) {
      log.error('Error fetching user details:', err);
      setUserName(`User #${userId}`);
      setUserDataLoaded(true);
    }
  }, [userId, accountId]);

  // Fetch user solutions
  const fetchSolutions = useCallback(async () => {
    if (!userId || !accountId) return;
    
    try {
      setLoading(true);
      log.log(`Fetching solutions for user ${userId} in account ${accountId}...`);
      
      const result = await solutionsService.getUserSolutions(Number(userId), Number(accountId));
      setSolutions(result.data);
      setError(null);
      
    } catch (err) {
      log.error('Error fetching user solutions:', err);
      setError('Failed to load solutions. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [userId, accountId]);

  // Initial data fetch
  useEffect(() => {
    if (accountId && userId) {
      fetchAccountName();
      fetchUserDetails();
      fetchSolutions();
    }
  }, [accountId, userId, fetchAccountName, fetchUserDetails, fetchSolutions]);

  // Handle back button
  const handleBack = useCallback(() => {
    navigate(`/accounts/${accountId}/users`);
  }, [navigate, accountId]);

  // Handle solution add dialog
  const handleOpenAddDialog = useCallback(() => {
    setAddDialogOpen(true);
  }, []);

  const handleCloseAddDialog = useCallback(() => {
    setAddDialogOpen(false);
  }, []);

  // Handle solution add success
  const handleSolutionAdded = useCallback(() => {
    // Refresh the list of solutions
    fetchSolutions();
    
    // Show success message
    setSuccessMessage("Solution access added successfully");
    
    // Close the dialog
    setAddDialogOpen(false);
  }, [fetchSolutions]);

  // Handle solution removal
  const handleRemoveSolution = useCallback(async (solutionId: number) => {
    if (!userId) return;
    
    try {
      await solutionsService.removeSolutionFromUser(Number(userId), solutionId);
      
      // Refresh the solutions list
      fetchSolutions();
      
      // Show success message
      setSuccessMessage("Solution access removed successfully");
    } catch (err) {
      log.error('Error removing solution:', err);
      setErrorMessage("Failed to remove solution access. Please try again.");
    }
  }, [userId, fetchSolutions]);

  // Handle snackbar close
  const handleCloseSuccessMessage = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSuccessMessage(null);
  };

  const handleCloseErrorMessage = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      <Breadcrumb 
        items={[
          {
            label: 'Accounts',
            path: '/accounts',
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          { 
            label: accountName,
            path: `/accounts/${accountId}/edit`,
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: 'Users',
            path: `/accounts/${accountId}/users`,
            icon: <PersonIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: `${userName} - Solutions`,
            icon: <PersonIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          }
        ]}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={handleBack} 
            sx={{ mr: 1, borderRadius: '8px' }}
            color="primary"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.primary.main,
            }}
          >
            Solutions for {userName}
          </Typography>
        </Box>
      </Box>
      
      {/* Loading skeleton displayed until user data is loaded */}
      {!userDataLoaded && (
        <Card
          elevation={2}
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
              <Skeleton variant="text" width={200} height={40} />
            </Box>
            <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={20} />
          </CardContent>
        </Card>
      )}
      
      {/* Content displayed only after user data is loaded */}
      {userDataLoaded && hasFullAccess && (
        <Card
          elevation={2}
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <AdminPanelSettingsIcon 
                color="primary"
                sx={{ 
                  fontSize: 40, 
                  mr: 2,
                  p: 1,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }}
              />
              <Typography variant="h5" component="h2" fontWeight={600}>
                {userRoleDisplay} Access
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              This user has the <strong>{userRoleDisplay}</strong> role, which grants automatic access to all solutions. Individual solution management is not required for this user.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Users with {userRoleDisplay} privileges have global access across the entire platform. No additional solution access configuration is necessary.
            </Typography>
          </CardContent>
        </Card>
      )}
      
      {userDataLoaded && !hasFullAccess && (
        <Paper 
          elevation={2}
          sx={{ 
            width: '100%', 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          <Box 
            p={2.5} 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Solution Access
            </Typography>
            
            <Button 
              variant="contained" 
              color="primary" 
              size="medium"
              onClick={handleOpenAddDialog}
              startIcon={<AddIcon />}
              sx={{ 
                px: 2,
                py: 1,
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                }
              }}
            >
              Add Solution
            </Button>
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em',
                      py: 1.5,
                      borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                      backgroundColor: theme.palette.background.default
                    }}
                  >
                    Solution Name
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em',
                      py: 1.5,
                      borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                      backgroundColor: theme.palette.background.default
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: alpha(theme.palette.background.default, 0.4),
                  }
                }}
              >
                {loading && (
                  <>
                    {[1, 2, 3].map((item) => (
                      <TableRow key={item}>
                        <TableCell>
                          <Skeleton variant="text" width="70%" height={24} />
                        </TableCell>
                        <TableCell align="center">
                          <Skeleton variant="circular" width={32} height={32} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
                
                {!loading && error && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ color: 'error.main' }}>
                      {error}
                    </TableCell>
                  </TableRow>
                )}
                
                {!loading && !error && solutions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          No solutions found for this user.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={handleOpenAddDialog}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          Add Solution
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                
                {!loading && !error && solutions.map((item) => (
                  <TableRow 
                    key={item.id}
                    sx={{ 
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {item.solution.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        onClick={() => handleRemoveSolution(item.solution.id)} 
                        color="error"
                        sx={{ 
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      
      {userDataLoaded && !hasFullAccess && (
        <AddSolutionDialog 
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          accountId={Number(accountId)}
          userId={Number(userId)}
          userName={userName}
          onSolutionAdded={handleSolutionAdded}
        />
      )}
      
      {/* Success Message */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccessMessage} 
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      
      {/* Error Message */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseErrorMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseErrorMessage} 
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserSolutionsPage;